/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 네이버·토스·쿠팡 제휴 URL/배너/iframe 붙여넣기 추출 (yesmoney affiliate-link 이식)
 * 작성/수정일: 2026-07-29
 */

const AFFILIATE_PREVIEW_HOSTS = [
  'naver.me',
  'brand.naver.com',
  'brandconnect.naver.com',
  'shopping.naver.com',
  'smartstore.naver.com',
  'm.smartstore.naver.com',
  'cr.shopping.naver.com',
  'toss.im',
  'toss.me',
  'service.toss.im',
  'link.coupang.com',
  'www.coupang.com',
  'coupang.com',
  'm.coupang.com',
  'coupa.ng',
];

function hostMatchesAllowed(host, allowed) {
  const h = host.toLowerCase();
  return h === allowed || h.endsWith(`.${allowed}`);
}

/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 허용 제휴 URL이면 정규화된 URL, 아니면 null
 * 작성/수정일: 2026-07-29
 */
export function parseAffiliatePreviewUrl(input) {
  const trimmed = String(input || '').trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase();
    if (!AFFILIATE_PREVIEW_HOSTS.some((allowed) => hostMatchesAllowed(host, allowed))) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function isAffiliatePreviewUrl(input) {
  return parseAffiliatePreviewUrl(input) !== null;
}

/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 평문에서 첫 제휴 URL 추출
 * 작성/수정일: 2026-07-29
 */
export function extractAffiliatePreviewUrlFromText(text) {
  const match = String(text || '').match(/https?:\/\/[^\s<>"']+/i);
  if (!match?.[0]) return null;
  const cleaned = match[0].replace(/[),.;]+$/g, '');
  return parseAffiliatePreviewUrl(cleaned);
}

/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 쿠팡 배너 HTML(a+img alt) → API 없이 카드 데이터
 * 작성/수정일: 2026-07-29
 */
export function extractCoupangBannerPreviewFromHtml(html) {
  if (!String(html || '').trim()) return null;

  const hrefMatch = html.match(
    /<a\b[^>]*\bhref=(["'])(https:\/\/link\.coupang\.com\/a\/[^"']+)\1[^>]*>[\s\S]*?<\/a>/i,
  );
  if (!hrefMatch?.[2]) return null;

  const anchorHtml = hrefMatch[0];
  const imgMatch = anchorHtml.match(
    /\b(?:src|data-src)=(["'])(https:\/\/[^"']*coupangcdn\.com\/image\/affiliate\/banner\/[^"']+)\1/i,
  );
  if (!imgMatch?.[2]) return null;

  const altMatch = anchorHtml.match(/\balt=(["'])([\s\S]*?)\1/i);
  const title = altMatch?.[2]?.trim() || '쿠팡 상품';

  let domain = 'link.coupang.com';
  try {
    domain = new URL(hrefMatch[2]).hostname.toLowerCase();
  } catch {
    // keep default
  }

  return {
    href: hrefMatch[2],
    title,
    description: title,
    image: imgMatch[2],
    domain,
  };
}

/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 쿠팡 iframe embed(coupa.ng) src 추출
 * 작성/수정일: 2026-07-29
 */
export function extractCoupangIframeUrlFromHtml(html) {
  if (!String(html || '').trim()) return null;
  const match = html.match(/<iframe\b[^>]*\bsrc=(["'])(https:\/\/coupa\.ng\/[^"']+)\1/i);
  if (!match?.[2]) return null;
  return parseAffiliatePreviewUrl(match[2]);
}

/**
 * [Gurumi 제휴·OG 링크 미리보기]
 * 역할: 붙여넣기 HTML/평문에서 제휴 미리보기 대상 추출
 * 작성/수정일: 2026-07-29
 */
export function extractAffiliatePreviewFromPaste(html, plain) {
  const banner =
    extractCoupangBannerPreviewFromHtml(html) ?? extractCoupangBannerPreviewFromHtml(plain);
  if (banner) {
    return { kind: 'card', card: banner };
  }

  const iframeUrl =
    extractCoupangIframeUrlFromHtml(html) ?? extractCoupangIframeUrlFromHtml(plain);
  if (iframeUrl) {
    return { kind: 'url', url: iframeUrl };
  }

  const affiliateUrl = extractAffiliatePreviewUrlFromText(plain);
  if (!affiliateUrl) return null;

  const trimmed = String(plain || '')
    .trim()
    .replace(/[),.;]+$/g, '');
  if (trimmed === affiliateUrl || trimmed === affiliateUrl.replace(/\/$/, '')) {
    return { kind: 'url', url: affiliateUrl };
  }

  return null;
}
